import { Form, message, Modal, Select } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import TextArea from 'antd/lib/input/TextArea';
import { locationsRestCrudThunksForSupplier } from '../../thunks/locations_thunks';
import { areasRestCrudThunksForSupplier } from '../../thunks/areas_thunks';
import { scheduleAssetTransfer, updateScheduledAssetTransfer } from '../../thunks/assets_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import ModalFormFooter from '../common/ModalFormFooter';
import {
	nullSafeGetOrElse,
	getDisabledDate,
	getDisabledDateTime,
} from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { DateTimeWithTimeZonev4 } from '../date_time_with_time_zone/DateTimeWithTimeZonev4';
import { wallTimeToUtcIso } from '../../utils/DataFormatterUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

const LOC_TC_NAME = 'scheduledTransferDropdown';
const AREA_TC_NAME = 'scheduledTransferDropdown';
const FORM_ID = 'bulk-transfer-location-form';

const BulkTransferToLocationModal: FC<any> = ({
	assetIds,
	transferId,
	initialValues,
	currentLocationId,
	onCancel,
	onSuccess,
	locations,
	areas,
	companyConfig,
	fetchLocations,
	fetchMultipleLocations,
	fetchAreas,
	fetchMultipleAreas,

	scheduleTransfer,
	updateTransfer,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [saving, setSaving] = useState(false);

	const transferNoteRequired = useMemo(
		() => nullSafeGetOrElse('inventoryConfig.transferNoteMandatory', companyConfig, false),
		[companyConfig]
	);

	const selectedLocationId = Form.useWatch('locationId', form);
	const triggerAtValue = Form.useWatch('triggerAt', form);

	const selectedLocation = useMemo(() => {
		if (!selectedLocationId) return null;
		const records = getRecordsForTargetCollection(locations, LOC_TC_NAME);
		return records.find((_) => _.id === selectedLocationId) || null;
	}, [selectedLocationId, locations]);

	const isFutureDate = useMemo(
		() => triggerAtValue && moment(triggerAtValue).isAfter(moment()),
		[triggerAtValue]
	);

	const transferTimezoneValue = Form.useWatch('transferTimezone', form);
	const selectedTimezone =
		transferTimezoneValue || nullSafeGetOrElse('timeZoneId', selectedLocation, moment.tz.guess());

	// Reset area whenever the location changes
	useEffect(() => {
		if (!selectedLocationId) return;
		form.setFieldsValue({ areaId: undefined });
	}, [selectedLocationId, form]);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);
			const triggerAt =
				values.triggerAt &&
				wallTimeToUtcIso(values.triggerAt, values.transferTimezone || moment.tz.guess());
			const entity = {
				triggerAt,
				assetIds,
				locationId: values.locationId,
				...(values.areaId && { areaId: values.areaId }),
				...(values.transferNote && { transferNote: values.transferNote }),
			};
			const promise = transferId ? updateTransfer(entity) : scheduleTransfer(entity);

			promise
				.then(onSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[assetIds, transferId, scheduleTransfer, updateTransfer, onSuccess]
	);

	console.log('initialValues', initialValues);
	return (
		<Modal
			visible={true}
			title={transferId ? 'Edit scheduled transfer' : 'Transfer to location'}
			onCancel={onCancel}
			footer={null}
			closable={true}
		>
			<Form
				initialValues={initialValues}
				id={FORM_ID}
				layout="vertical"
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name="locationId"
					label="Location"
					rules={[{ required: true, message: 'Location is required!' }]}
				>
					<OWAsyncSelect
						stateSlice={locations}
						targetCollectionName={LOC_TC_NAME}
						fetchMultiple={fetchMultipleLocations}
						fetchData={(searchText, targetCollectionName) =>
							fetchLocations({ name: searchText }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option
								key={record.id}
								value={record.id}
								disabled={record.id === currentLocationId}
							>
								{record.name}
							</Select.Option>
						)}
					/>
				</Form.Item>
				<Form.Item name="areaId" label="Area">
					<OWAsyncSelect
						disabled={!selectedLocationId}
						stateSlice={areas}
						targetCollectionName={AREA_TC_NAME}
						fetchMultiple={fetchMultipleAreas}
						fetchData={(searchText, targetCollectionName) =>
							fetchAreas({ name: searchText, locationId: selectedLocationId }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option key={record.id} value={record.id}>
								{record.name}
							</Select.Option>
						)}
						additionalFilters={{ locationId: selectedLocationId }}
					/>
				</Form.Item>
				<div className="mb-6">
					<DateTimeWithTimeZonev4
						form={form}
						location={selectedLocation}
						displayName="Effective transfer date"
						dateName="triggerAt"
						timeZoneName="transferTimezone"
						rules={[
							{ required: true, message: 'Transfer date is required.' },
							{
								validator: (_, value) => {
									if (!value) return Promise.resolve();
									const tz = form.getFieldValue('transferTimezone') || moment.tz.guess();
									const utcIso = wallTimeToUtcIso(value, tz);
									if (moment(utcIso).isBefore(moment().subtract(1, 'minute'))) {
										return Promise.reject(
											new Error('Transfer date and time must not be in the past.')
										);
									}
									return Promise.resolve();
								},
							},
						]}
						help={'Future-dated transfers will be executed on the chosen date & time.'}
						disabledDate={getDisabledDate(moment().toISOString(), selectedTimezone)}
						disabledTime={getDisabledDateTime(
							moment().toISOString(),
							selectedTimezone,
							undefined,
							15
						)}
						minuteStep={15}
						initialTimeZone={selectedLocation ? selectedLocation.timeZoneId : undefined}
					/>
				</div>
				<Form.Item
					name="transferNote"
					label="Transfer Note"
					rules={[{ required: transferNoteRequired, message: 'Please enter transfer note.' }]}
				>
					<TextArea autoSize={{ minRows: 3 }} />
				</Form.Item>
				<ModalFormFooter
					okText={isFutureDate ? 'Schedule Transfer' : 'Transfer'}
					cancelText="Cancel"
					loading={saving}
					formId={FORM_ID}
					onCancelClick={onCancel}
				/>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	locations: state.locations,
	areas: state.areas,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchLocations: (params, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAreas: (params, targetCollectionName) =>
		dispatch(areasRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleAreas: (ids, targetCollectionName) =>
		dispatch(areasRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	scheduleTransfer: (data) => dispatch(scheduleAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
	updateTransfer: (data) => dispatch(updateScheduledAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkTransferToLocationModal);
